import { computed, ref } from 'vue'
import { contentProvider } from '#root/renderer/contentProvider'
import { computedAsync, useMemoize } from '@vueuse/core'
import { createInjectionState } from '@vueuse/shared'
import { useCartSession } from '#root/lib/useCartSession'
import { DateTime } from 'luxon'
import type {
  FindOccasionsResponse,
  OccasionFeedItem
} from '#root/types/occasions'

const [useProvideOccasionsStore, useOccasionsStore] = createInjectionState(
  () => {
    const { testMode } = useCartSession()
    const isFetching = ref(false)
    const fetchTime = ref(new Date().getTime())

    const findOccasions = useMemoize(
      async (cacheKey: number): Promise<FindOccasionsResponse> => {
        void cacheKey // used as memo cache key by useMemoize
        const raw = await contentProvider.findOccasions(true, testMode.value)
        return raw as FindOccasionsResponse
      }
    )

    const showPast = ref(false)

    const data = computedAsync(
      async () => findOccasions(fetchTime.value),
      null as FindOccasionsResponse | null,
      {
        evaluating: isFetching,
        lazy: true
      }
    )

    const indexes = computed(() => data.value?.indexes)
    const uniqueIndexes = computed(() => data.value?.unique_indexes)
    const items = computed(() => data.value?.data ?? null)

    const upcomingIndicies = computed(() => {
      const indicies: number[] = []
      const list = items.value
      if (list) {
        list.forEach((item, i) => {
          if (isUpcoming(item, fetchTime.value)) indicies.push(i)
        })
      }
      return indicies
    })

    const upcomingItems = computed(() => {
      const list = items.value
      const ind = upcomingIndicies.value
      return list ? ind.map(i => list[i]) : []
    })

    const yearMonthIndex = computed(() => {
      const list = items.value
      if (!list) return {} as Record<string, number[]>
      return list.reduce<Record<string, number[]>>((acc, cur, i) => {
        const starts = DateTime.fromISO(
          cur.starts_at_utc ?? cur.starts_at ?? ''
        )

        if (starts.valueOf() - fetchTime.value > 0 || showPast.value) {
          const key = starts.toLocal().toFormat('yyyy-LL')
          if (acc[key] === undefined) acc[key] = []
          acc[key].push(i)
        }
        return acc
      }, {})
    })

    function isUpcoming(item: OccasionFeedItem, time: number): boolean {
      const startTime = item.starts_at_utc
        ? new Date(item.starts_at_utc).getTime()
        : item.starts_at
          ? new Date(item.starts_at).getTime()
          : 0
      return (
        (startTime - time > 0 &&
          item.schedule_status === 'active' &&
          !!item.stripe_floor_price_id &&
          !!item.stripe_list_price_id) ||
        showPast.value
      )
    }

    function refetchData(): void {
      findOccasions.clear()
      fetchTime.value = new Date().getTime()
    }

    return {
      fetchTime,
      findOccasions,
      indexes,
      isFetching,
      isUpcoming,
      items,
      refetchData,
      showPast,
      uniqueIndexes,
      upcomingIndicies,
      upcomingItems,
      yearMonthIndex
    }
  }
)

function useOccasionsStoreOrThrow() {
  const occasionsStore = useOccasionsStore()
  if (occasionsStore == null)
    throw new Error(
      'Please call `useProvideOccasionsStore` on the appropriate parent component'
    )
  return occasionsStore
}

export { useProvideOccasionsStore, useOccasionsStoreOrThrow }
